import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { Avatar, Input } from "@/shared/ui";
import { useGetChatPeopleQuery } from "../api";
import type { ChatPerson } from "../types";

interface Props {
  value: number[];
  onChange: (ids: number[]) => void;
}

export default function ChatChannelMembersTab({ value, onChange }: Props) {
  const { t, i18n } = useTranslation("ChatPage");
  const locale = i18n.language as "ru" | "kk" | "en";
  const [search, setSearch] = useState("");
  const { data: people = [], isLoading } = useGetChatPeopleQuery();

  const selectedSet = useMemo(() => new Set(value), [value]);

  const filtered = useMemo(() => {
    if (!search.trim()) return people;
    const q = search.trim().toLowerCase();
    return people.filter((p) => p.full_name.toLowerCase().includes(q) || p.email.toLowerCase().includes(q));
  }, [people, search]);

  function getPersonSubtitle(person: ChatPerson): string {
    if (locale !== "en") {
      const position = locale === "kk" ? person.job_position_kk : person.job_position_ru;
      if (position) return position;
    }
    return t(`channelForm.members.fallback.${person.user_type}`);
  }

  function handleToggle(userId: number) {
    if (selectedSet.has(userId)) {
      onChange(value.filter((id) => id !== userId));
    } else {
      onChange([...value, userId]);
    }
  }

  if (isLoading) {
    return (
      <div className="py-8 text-center text-body-regular-sm content-base-secondary">
        {t("channelForm.members.loading")}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <Input
        placeholder={t("channelForm.members.search")}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <div className="flex flex-col gap-2 max-h-72 overflow-y-auto component-scroll-thin pr-1">
        {filtered.length === 0 ? (
          <p className="text-body-regular-sm content-base-secondary text-center py-4">
            {search.trim() ? t("channelForm.members.noResults") : t("channelForm.members.empty")}
          </p>
        ) : (
          filtered.map((person) => {
            const isChecked = selectedSet.has(person.user_id);
            return (
              <button
                key={person.user_id}
                type="button"
                onClick={() => handleToggle(person.user_id)}
                className="w-full flex items-center gap-3 px-1 py-1.5 radius-sm hover:surface-secondary-fill transition-colors text-left cursor-pointer">
                <Avatar name={person.full_name} textClassName="text-label-xs" />
                <div className="flex-1 min-w-0 flex flex-col">
                  <span className="text-body-bold-sm content-base-primary truncate">{person.full_name}</span>
                  <span className="text-body-regular-xs content-base-secondary truncate">{getPersonSubtitle(person)}</span>
                </div>
                <span
                  className={cn(
                    "w-4 h-4 shrink-0 radius-2xs border flex items-center justify-center transition-colors",
                    isChecked ? "surface-brand-fill border-transparent" : "surface-base-fill surface-base-stroke",
                  )}>
                  {isChecked && <span className="text-xs font-bold content-static-white leading-none">✓</span>}
                </span>
              </button>
            );
          })
        )}
      </div>
      {value.length > 0 && (
        <p className="text-body-regular-xs content-base-secondary">{t("channelForm.members.selected", { count: value.length })}</p>
      )}
    </div>
  );
}
